import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionButton,
  CommandBar,
  ICommandBar,
  ICommandBarItemProps,
  Stack,
} from "@fluentui/react";
import { debounce, useTheme } from "cayo.ui";
import styled from "styled-components";
import { ExportConfig } from "@cayo/shared/api/types/schema";
import { AppContext } from "@cayo/shared/components/App/appContext";
import { newQuery, setCondition } from "@cayo/shared/components/GridContainer/gridModel/Query";
import {
  FilterItem,
  ConditionType,
  IQuery,
  DispatchAction,
} from "@cayo/shared/components/GridContainer/types";
import type { ExportFileHandler } from "@cayo/shared/services/export";
import { IFilterBar } from "../../api/schema.api";
import AnrSearchBox from "../AnrSearchBox";
import globalHooks from "../GlobalHooks";
import { useToggleNavCollapsed } from "../Nav/nav-hooks";
import { ExportButton } from "./components/ExportButton/ExportButton";
import { ApplyFilterButton } from "./GridFilterBar.ApplyFilterButton";
import { useGridContextMenu } from "./hooks/useGridContextMenu";
import { FilterItemButton } from "./ModernGridFilterBar.FilterItemButton";
import { FilterPanel } from "./ModernGridFilterBar.FilterPanel";
import { convertFilterItemsToObject, getFilterItems } from "./ModernGridFilterBar.utils";

type ModernGridFilterBarProps = {
  scheme: IFilterBar;
  query: IQuery;
  showSearchButton: boolean;
  exportDisabled: boolean;
  exportConfig?: ExportConfig;
  hideSearchText?: boolean;
  dispatch: DispatchAction;
  onExport: ExportFileHandler;
};

export const ModernGridFilterBar: FC<ModernGridFilterBarProps> = ({
  scheme,
  dispatch,
  query,
  exportConfig,
  exportDisabled,
  onExport,
  showSearchButton,
  hideSearchText,
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [filterItems, setFilterItems] = useState<FilterItem[]>([]);
  const { officeTheme } = useTheme();

  // TODO: send global event like resize
  const { collapsed } = useToggleNavCollapsed();
  const { responsiveMode } = useContext(AppContext);

  useEffect(() => {
    setIsDirty(false);
    setFilterItems(getFilterItems(query));
  }, [query]);

  const onApplyFilter = useCallback(
    (items: FilterItem[]) => {
      setFilterItems([...items]);
      const conditionQuery = setCondition(query ?? newQuery(), {
        kind: ConditionType.ModernFilter,
        value: items,
      });
      dispatch({ kind: "changeQuery", query: conditionQuery });
    },
    [query]
  );

  const onSave = useCallback(
    (items: FilterItem[]) => {
      onApplyFilter(items);
      setShowPanel(false);
    },
    [onApplyFilter]
  );

  const onRemoveFilterButton = useCallback(
    (i: number) => {
      filterItems?.splice(i, 1);
      setIsDirty(true);
      setFilterItems([...filterItems!]);
    },
    [filterItems, onSave]
  );

  const object = useMemo(() => {
    const result = convertFilterItemsToObject(filterItems);
    return result;
  }, [filterItems]);

  const { gridContextMenu } = useGridContextMenu({
    dispatch,
    query,
    allowClear: true,
    groupConfig: [],
    previewQueryId: null,
    onExport,
    exportDisabled,
    exportConfig,
  });

  const itemsInfo = useMemo(() => {
    const result = filterItems.map<ICommandBarItemProps>((f, i) => ({
      key: f.name,
      text: f.label,
      buttonStyles: { root: { backgroundColor: "transparent !important" } },
      onRender: () => {
        return (
          <FilterItemButton
            onClick={() => onRemoveFilterButton(i)}
            key={f.name}
            name={f.name}
            value={f.value}
            displayValue={f.displayValue}
            label={f.label}
          />
        );
      },
    }));

    const showExportButton = exportConfig?.enabled && exportConfig.formats.length > 0;
    const addExportButton = {
      key: "exportButton",
      onRender: () => {
        if (!showExportButton) {
          return null;
        }

        return (
          <ExportButton exportConfig={exportConfig} onExport={onExport} disabled={exportDisabled} />
        );
      },
    };

    const farItems: ICommandBarItemProps[] = [];
    const addFilterButton = {
      key: "addFilter",
      text: scheme.title,
      onRender: () => {
        return (
          <ActionButton
            styles={{
              root: {
                padding: "0 6px",
                border: `1px solid ${officeTheme.semanticColors.buttonBorder}`,
                borderRadius: 18,
                height: 28,
                whiteSpace: "nowrap",
              },
            }}
            iconProps={{ iconName: "Filter" }}
            onClick={() => setShowPanel(true)}
          >
            {scheme.title}
          </ActionButton>
        );
      },
    };

    farItems.push(addFilterButton);
    farItems.push(addExportButton);
    farItems.push(gridContextMenu);

    return { items: result, farItems };
  }, [filterItems, responsiveMode, exportConfig, exportDisabled, onExport]);

  const commandBarRef = useRef<ICommandBar>(null);

  globalHooks.useWindowResize(() => debounce(() => commandBarRef?.current?.remeasure(), 50)());

  return (
    <>
      <Stack
        horizontal
        verticalAlign="center"
        horizontalAlign="start"
        tokens={{ childrenGap: 4 }}
        key={`ModernFilterBar_${responsiveMode}_${collapsed}`}
        styles={{
          root: {
            backgroundColor: officeTheme.palette.neutralLighter,
            paddingLeft: 4,
          },
        }}
      >
        <AnrSearchBox
          key="anr-sb"
          width={200}
          query={query}
          dispatch={dispatch}
          showSearchButton={showSearchButton}
          hideSearchText={hideSearchText}
        />
        {(!!itemsInfo.items.length || !!itemsInfo.farItems.length) && (
          <StyledCommandBar
            items={itemsInfo.items}
            farItems={itemsInfo.farItems}
            componentRef={commandBarRef}
            overflowButtonProps={{ styles: { root: { backgroundColor: "transparent" } } }}
            styles={{
              root: {
                backgroundColor: "transparent",
                alignItems: "center",
                padding: 0,
                width: "100%",
              },
              primarySet: {
                gridGap: "4px",
                flexGrow: 0,
              },
              secondarySet: {
                marginLeft: "4px",
                flexGrow: 1,
                justifyContent: "flex-start",
                alignItems: "center",
                gridGap: "4px",

                selectors: {
                  "> :nth-child(2)": {
                    marginRight: "auto",
                  },
                },
              },
            }}
          />
        )}
      </Stack>

      {isDirty && <ApplyFilterButton apply={() => onApplyFilter(filterItems)} />}

      {showPanel && (
        <FilterPanel
          title={scheme.title}
          items={scheme.items}
          onSave={onSave}
          onClose={() => setShowPanel(false)}
          object={object}
        />
      )}
    </>
  );
};

const StyledCommandBar = styled(CommandBar)`
  background-color: transparent;
  width: 100%;

  .ms-ContextualMenu {
    background-color: transparent;
  }
`;
